// ManDana - the civil day / Fajr day bridge
//
// Prayer calculation belongs to md-theme.jsx. This module does not calculate,
// round, or replace a prayer time; it only lays the values md-theme returns onto
// one continuous timeline. That distinction matters between midnight and Fajr,
// when the civil date has changed but the Fajr-to-Fajr day is still in progress.

interface MdCycleState {
  anchorOffset: number;
  cycles: number;
  expanded: boolean;
  nowMin: number;
}

interface MdTimelineInput {
  anchorOffset: number;
  cycles: number;
  dayItemsFor: (offset: number) => any[];
  eventsByDay: Record<string, any[]>;
  dayKeyFor: (offset: number) => string;
}

interface MdTimelineResult {
  items: any[];
  startMin: number | null;
  endMin: number | null;
  closingFajr: any | null;
}

interface MdGridCycleResult {
  events: any[];
  prayers: any[];
  startMin: number | null;
  endMin: number | null;
}

(function (g: any) {
  'use strict';

  const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

  /** The civil date changes at midnight; the active Fajr day changes at Fajr. */
  function mdCycleState(selectedOffset: unknown, nowMin: unknown, todayFajr: unknown): MdCycleState {
    const selected = finite(selectedOffset) ? selectedOffset : 0;
    const clock = finite(nowMin) ? nowMin : 0;
    const beforeFajr = selected === 0 && finite(todayFajr) && clock < todayFajr;
    return {
      anchorOffset: beforeFajr ? -1 : selected,
      cycles: beforeFajr ? 2 : 1,
      expanded: beforeFajr,
      nowMin: beforeFajr ? clock + 1440 : clock,
    };
  }

  function prayer(items: any[], id: string): any | null {
    return items.find((item) => item && item.kind === 'prayer' && item.id === id) || null;
  }

  function timelineItem(item: any, offset: number, shift: number, dayKey: string): any | null {
    if (!item || typeof item !== 'object' || !finite(item.min)) return null;
    const uid = typeof item.uid === 'string' && item.uid ? item.uid
      : typeof item.id === 'string' && item.id ? item.id : '';
    if (!uid) return null;
    return {
      ...item,
      min: item.min + shift,
      _mdSourceMin: item.min,
      _mdSourceOffset: offset,
      _mdSourceDay: dayKey,
      _mdTimelineKey: `${item.kind || 'item'}:${dayKey}:${uid}`,
    };
  }

  /** Build one or two Fajr cycles from civil-date documents. */
  function mdBuildTimeline(input: MdTimelineInput): MdTimelineResult {
    const anchor = finite(input && input.anchorOffset) ? input.anchorOffset : 0;
    const cycles = input && input.cycles === 2 ? 2 : 1;
    const dayItemsFor = input && input.dayItemsFor;
    const dayKeyFor = input && input.dayKeyFor;
    const eventsByDay = input && input.eventsByDay && typeof input.eventsByDay === 'object'
      ? input.eventsByDay : {};
    if (typeof dayItemsFor !== 'function' || typeof dayKeyFor !== 'function') {
      return { items: [], startMin: null, endMin: null, closingFajr: null };
    }

    const days = new Map<number, any[]>();
    for (let offset = anchor; offset <= anchor + cycles; offset++) {
      const value = dayItemsFor(offset);
      days.set(offset, Array.isArray(value) ? value : []);
    }

    const opening = prayer(days.get(anchor) || [], 'fajr');
    const closingRaw = prayer(days.get(anchor + cycles) || [], 'fajr');
    if (!opening || !closingRaw || !finite(opening.min) || !finite(closingRaw.min)) {
      // No invented dawn. A partial polar day keeps the existing single-day
      // rendering rather than acquiring a boundary the prayer engine did not give.
      const key = dayKeyFor(anchor);
      const fallback = [...(days.get(anchor) || []), ...(eventsByDay[key] || [])]
        .map((item) => timelineItem(item, anchor, 0, key)).filter(Boolean)
        .sort((a: any, b: any) => a.min - b.min);
      return { items: fallback, startMin: null, endMin: null, closingFajr: null };
    }

    const startMin = opening.min;
    const endMin = cycles * 1440 + closingRaw.min;
    const out: any[] = [];

    for (let offset = anchor; offset <= anchor + cycles; offset++) {
      const shift = (offset - anchor) * 1440;
      const dayKey = dayKeyFor(offset);

      if (offset > anchor) {
        out.push({
          kind: 'civil-boundary', min: shift, uid: `midnight-${dayKey}`,
          _mdSourceOffset: offset, _mdSourceDay: dayKey,
          _mdTimelineKey: `midnight:${dayKey}`,
        });
      }

      for (const item of days.get(offset) || []) {
        const placed = timelineItem(item, offset, shift, dayKey);
        if (placed && placed.min >= startMin && placed.min < endMin) out.push(placed);
      }
      const events = Array.isArray(eventsByDay[dayKey]) ? eventsByDay[dayKey] : [];
      for (const event of events) {
        const placed = timelineItem(event, offset, shift, dayKey);
        if (placed && placed.min >= startMin && placed.min < endMin) out.push(placed);
      }
    }

    out.sort((a, b) => {
      if (a.min !== b.min) return a.min - b.min;
      const ap = a.kind === 'civil-boundary' ? 0 : 1;
      const bp = b.kind === 'civil-boundary' ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return String(a._mdTimelineKey).localeCompare(String(b._mdTimelineKey));
    });

    const closingDay = dayKeyFor(anchor + cycles);
    const closingFajr = timelineItem(closingRaw, anchor + cycles, cycles * 1440, closingDay);
    return { items: out, startMin, endMin, closingFajr };
  }

  /** Remove timeline-only coordinates before a record returns to an editor/store. */
  function mdTimelineSource(item: unknown): { item: any; offset: number | null; dayKey: string | null } {
    if (!item || typeof item !== 'object') return { item, offset: null, dayKey: null };
    const src = item as any;
    const clean = { ...src };
    if (finite(src._mdSourceMin)) clean.min = src._mdSourceMin;
    for (const key of ['_mdSourceMin', '_mdSourceOffset', '_mdSourceDay', '_mdTimelineKey']) delete clean[key];
    return {
      item: clean,
      offset: finite(src._mdSourceOffset) ? src._mdSourceOffset : null,
      dayKey: typeof src._mdSourceDay === 'string' ? src._mdSourceDay : null,
    };
  }

  /** One editable Fajr cycle, with the post-midnight civil date on the same axis. */
  function mdBuildGridCycle(input: MdTimelineInput): MdGridCycleResult {
    const timeline = mdBuildTimeline({ ...input, cycles: 1 });
    const prayers = timeline.items.filter((item) => item.kind === 'prayer');
    if (timeline.closingFajr) {
      prayers.push({ ...timeline.closingFajr, uid: 'fajr-next', nextDay: true });
    }
    return {
      events: timeline.items.filter((item) => item.kind === 'event'),
      prayers,
      startMin: timeline.startMin,
      endMin: timeline.endMin,
    };
  }

  /** Translate a grid-axis record back to the civil date and wall-clock minute it occupies. */
  function mdGridSource(item: unknown, anchorOffset: unknown, dayKeyFor?: (offset: number) => string) {
    if (!item || typeof item !== 'object') return { item, offset: null, dayKey: null };
    const src = item as any;
    const axisMin = finite(src.min) ? src.min : 0;
    const anchor = finite(anchorOffset) ? anchorOffset : 0;
    const shift = Math.floor(axisMin / 1440);
    const offset = anchor + shift;
    const clean = { ...src, min: ((axisMin % 1440) + 1440) % 1440 };
    for (const key of ['_mdSourceMin', '_mdSourceOffset', '_mdSourceDay', '_mdTimelineKey']) delete clean[key];
    return {
      item: clean,
      offset,
      dayKey: typeof dayKeyFor === 'function' ? dayKeyFor(offset) : null,
    };
  }

  /* ── THE ONE PIECE OF RESIDUE A DISCARDED SHEET CAN LEAVE (P-7) ───────────
     A block drawn on the grid is painted BEFORE the compose sheet opens, so
     you can see what you drew underneath it. That block is not the record —
     it is a promise that one is coming — and under P-5 the record only exists
     when it is confirmed. So a sheet that closes without creating anything has
     to take the block with it, or the grid keeps a block for a record nobody
     made (rule 3: nothing you didn't do leaves residue).

     `pendingUid` is what was drawn; `live` is md-app's last commit, if any.
     The block stays only when that commit IS this record and wrote something. */
  function mdComposeGridDrop(pendingUid: unknown, live: unknown): string | null {
    if (typeof pendingUid !== 'string' || !pendingUid) return null;
    const last = live as any;
    if (last && last.rec && last.rec.uid === pendingUid && !last.blank) return null;
    return pendingUid;
  }

  /** Replace only the records inside one Fajr cycle, preserving both adjacent dates outside it. */
  function mdCommitGridCycle(input: MdTimelineInput & { draft: any[]; startMin: number; endMin: number }) {
    const anchor = finite(input && input.anchorOffset) ? input.anchorOffset : 0;
    const start = finite(input && input.startMin) ? input.startMin : 0;
    const end = finite(input && input.endMin) ? input.endMin : 1440;
    const dayKeyFor = input && input.dayKeyFor;
    const source = input && input.eventsByDay && typeof input.eventsByDay === 'object' ? input.eventsByDay : {};
    if (typeof dayKeyFor !== 'function') return source;

    const firstKey = dayKeyFor(anchor);
    const nextKey = dayKeyFor(anchor + 1);
    const nextFajr = end - 1440;
    const first = (source[firstKey] || []).filter((item) => !finite(item.min) || item.min < start);
    const next = (source[nextKey] || []).filter((item) => !finite(item.min) || item.min >= nextFajr);

    for (const item of Array.isArray(input.draft) ? input.draft : []) {
      const placed = mdGridSource(item, anchor, dayKeyFor);
      if (!placed.item || placed.offset == null) continue;
      (placed.offset <= anchor ? first : next).push(placed.item);
    }

    return {
      ...source,
      [firstKey]: first.sort((a: any, b: any) => (a.min || 0) - (b.min || 0)),
      [nextKey]: next.sort((a: any, b: any) => (a.min || 0) - (b.min || 0)),
    };
  }

  Object.assign(g, { mdCycleState, mdBuildTimeline, mdTimelineSource,
    mdBuildGridCycle, mdGridSource, mdComposeGridDrop, mdCommitGridCycle });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = { mdCycleState, mdBuildTimeline, mdTimelineSource,
    mdBuildGridCycle, mdGridSource, mdComposeGridDrop, mdCommitGridCycle };
})(typeof window !== 'undefined' ? window : globalThis);
